import { useTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryStack,
  VictoryTheme,
  VictoryLabel,
  VictoryScatter
} from 'victory';
import { numberAbbreviatedTickFormat } from '~/modules/common/charts/dashboard';
import {
  useIsBreakpointDown,
  useThemeBreakpoint
} from '~/modules/common/hooks';
import { useVictoryChartContainerStyles } from '~/modules/common/charts/dashboard/config';
import { CHART_KEYS } from '../../hooks/data-conversion';
import { PROBLEM_FILTER_OPTIONS } from '../ProblemFilterDropdown';
import BenchOverAllocationChartSkeleton from './BenchOverAllocationChartSkeleton';
import { tooltipTranslationKeys, useTooltipLabels } from './useTooltipLabels';
import { createBOAChartTooltipContainer } from './BOAChartTooltipContainer';
import CustomVictoryYAxisLabel from './CustomVictoryYAxisLabel';

const BAR_DIMENSIONS = {
  HIGHLIGHTED_THICKNESS: 32,
  DEFAULT_THICKNESS: 24,
  SPACING_UNITS: 1.5,
  STROKE_WIDTH: 3
};

const CHART_BAR_FIELDS = [
  CHART_KEYS.BENCH,
  CHART_KEYS.NOT_WORKING,
  CHART_KEYS.FITTING_ALLOCATION,
  CHART_KEYS.OVER_ALLOCATION
];

const CHART_CONFIGS = {
  xs: { width: 400 },
  sm: { width: 800 },
  md: { width: 800 },
  lg: { width: 1000 },
  xl: { width: 1200 }
};

const calculateDynamicHeight = (theme, barCount) => {
  const barHeight =
    BAR_DIMENSIONS.HIGHLIGHTED_THICKNESS +
    theme.spacing(BAR_DIMENSIONS.SPACING_UNITS);
  const chartPadding = theme.spacing(9);

  return barCount * barHeight + chartPadding;
};

const mapToVictoryData = (data, field) =>
  data.map(item => ({
    x: item.label.toString(),
    y: item[field]
  }));

const getChartData = data => ({
  chartFields: CHART_BAR_FIELDS,
  stackedBars: CHART_BAR_FIELDS.map(chartField =>
    mapToVictoryData(data, chartField)
  ),
  totalResources: mapToVictoryData(data, CHART_KEYS.TOTAL_RESOURCES)
});

const getBarStyle = ({
  barKey,
  highlightedBarKey,
  barColors,
  barStrokeColors
}) => {
  const isHighlighted = barKey === highlightedBarKey;

  return {
    data: {
      fill: barColors[barKey],
      stroke: ({ datum }) =>
        isHighlighted && datum.y > 0 ? barStrokeColors[barKey] : 'none',
      strokeWidth: ({ datum }) =>
        isHighlighted && datum.y > 0 ? BAR_DIMENSIONS.STROKE_WIDTH : 0
    }
  };
};

const getBarWidth = (barKey, highlightedBarKey) =>
  barKey === highlightedBarKey
    ? BAR_DIMENSIONS.HIGHLIGHTED_THICKNESS
    : BAR_DIMENSIONS.DEFAULT_THICKNESS;

const useChartStyles = ({ barCount }) => {
  const theme = useTheme();
  const breakpoint = useThemeBreakpoint(theme);
  const config = CHART_CONFIGS[breakpoint] || CHART_CONFIGS.md;
  const isMobile = useIsBreakpointDown('sm');
  const axisLabelFontSize = isMobile ? 13 : 15;

  const barColors = {
    [CHART_KEYS.BENCH]: theme.palette.resourceAllocation.bench,
    [CHART_KEYS.NOT_WORKING]: theme.palette.resourceAllocation.timeOff,
    [CHART_KEYS.FITTING_ALLOCATION]: theme.palette.resourceAllocation.allocated,
    [CHART_KEYS.OVER_ALLOCATION]: theme.palette.resourceAllocation.overAllocated
  };

  const barStrokeColors = {
    [CHART_KEYS.BENCH]: theme.palette.resourceAllocation.benchStroke,
    [CHART_KEYS.OVER_ALLOCATION]:
      theme.palette.resourceAllocation.overAllocationStroke
  };

  return {
    chart: {
      width: config.width,
      height: calculateDynamicHeight(theme, barCount),
      domain: [0, 10],
      domainPadding: { x: theme.spacing(2.5) },
      padding: {
        top: theme.spacing(2),
        bottom: theme.spacing(7),
        left: isMobile ? theme.spacing(18) : theme.spacing(25),
        right: theme.spacing(2)
      }
    },
    barColors,
    barStrokeColors,
    totalResourcesMarker: {
      offset: theme.spacing(2.5),
      style: {
        data: {
          stroke: theme.palette.resourceAllocation.totalResources,
          strokeWidth: 7,
          strokeDasharray: '2,2'
        }
      }
    },
    axis: {
      x: {
        tickLabels: {
          fontFamily: theme.typography.fontFamily,
          fontSize: axisLabelFontSize,
          fontWeight: theme.typography.fontWeightRegular,
          fill: theme.palette.text.primary,
          padding: theme.spacing(1)
        },
        grid: {
          stroke: theme.palette.grey[300],
          strokeDasharray: '2,2',
          strokeWidth: 1
        },
        axis: {
          stroke: 'transparent',
          strokeWidth: 0
        }
      },
      y: {
        tickLabels: {
          fontFamily: theme.typography.fontFamily,
          fontSize: axisLabelFontSize,
          fontWeight: theme.typography.fontWeightRegular,
          fill: theme.palette.text.primary,
          padding: theme.spacing(2),
          textAnchor: 'end',
          cursor: 'pointer',
          backgroundPadding: {
            left: theme.spacing(1.25),
            right: theme.spacing(1.25),
            top: theme.spacing(0.625),
            bottom: theme.spacing(0.625)
          }
        },
        ticks: {
          stroke: 'transparent',
          size: 0
        },
        grid: {
          stroke: 'none'
        },
        axis: {
          stroke: 'transparent',
          strokeWidth: 0
        }
      }
    },
    tooltip: {
      maxChars: isMobile ? 50 : 125
    }
  };
};

const TotalResourcesMarker = ({ x, y, offset, style }) => {
  return <line x1={x} y1={y - offset} x2={x} y2={y + offset} {...style} />;
};

const tickLabelComponent = <VictoryLabel />;

const containerStyle = { direction: 'ltr' };

export const BenchOverAllocationChart = ({
  availabilityData,
  loading,
  selectedProblem = PROBLEM_FILTER_OPTIONS.BENCH
}) => {
  const intl = useIntl();
  const history = useHistory();
  const styles = useChartStyles({
    barCount: availabilityData.length
  });
  const victoryContainerClasses = useVictoryChartContainerStyles();

  const {
    chartData,
    chartFields,
    stackedBars,
    totalResources
  } = useMemo(() => {
    const chartDisplayData = [...availabilityData].reverse();

    return {
      chartData: chartDisplayData,
      ...getChartData(chartDisplayData)
    };
  }, [availabilityData]);

  const xTickFormat = useCallback(
    tick => numberAbbreviatedTickFormat({ intl, tick: Math.abs(tick) }),
    [intl]
  );

  const getTooltipLabels = useTooltipLabels({
    availabilityData,
    maxChars: styles.tooltip.maxChars
  });

  const containerComponent = useMemo(
    () =>
      createBOAChartTooltipContainer({
        labels: getTooltipLabels,
        voronoiPadding: styles.chart.padding,
        voronoiBlacklist: [...chartFields.slice(1), CHART_KEYS.TOTAL_RESOURCES],
        selectedLabel: intl.formatMessage(
          { id: tooltipTranslationKeys[selectedProblem] },
          { value: '' }
        )
      }),
    [getTooltipLabels, styles.chart.padding, chartFields, intl, selectedProblem]
  );

  const yTickFormat = useCallback((_, index) => chartData[index]?.label, [
    chartData
  ]);

  const handleYAxisLabelClick = useCallback(
    () => history.push('/quick-allocation'),
    [history]
  );

  const clickableYAxisTickLabelComponent = useMemo(
    () => (
      <CustomVictoryYAxisLabel
        maxWidth={styles.chart.padding.left}
        onLabelClick={handleYAxisLabelClick}
      />
    ),
    [handleYAxisLabelClick, styles.chart.padding.left]
  );

  const totalResourcesMarkerComponent = useMemo(
    () => <TotalResourcesMarker offset={styles.totalResourcesMarker.offset} />,
    [styles.totalResourcesMarker.offset]
  );

  if (loading) return <BenchOverAllocationChartSkeleton />;

  return (
    <div className={victoryContainerClasses.container} style={containerStyle}>
      <VictoryChart
        horizontal
        theme={VictoryTheme.material}
        height={styles.chart.height}
        width={styles.chart.width}
        padding={styles.chart.padding}
        domainPadding={styles.chart.domainPadding}
        containerComponent={containerComponent}
      >
        <VictoryAxis
          dependentAxis
          domain={styles.chart.domain}
          style={styles.axis.x}
          orientation="bottom"
          tickFormat={xTickFormat}
          tickLabelComponent={tickLabelComponent}
          crossAxis={false}
        />
        <VictoryAxis
          style={styles.axis.y}
          tickFormat={yTickFormat}
          tickLabelComponent={clickableYAxisTickLabelComponent}
          orientation="left"
        />
        <VictoryStack>
          {chartFields.map((barKey, index) => (
            <VictoryBar
              key={barKey}
              name={barKey}
              data={stackedBars[index]}
              barWidth={getBarWidth(barKey, selectedProblem)}
              style={getBarStyle({
                barKey,
                highlightedBarKey: selectedProblem,
                barColors: styles.barColors,
                barStrokeColors: styles.barStrokeColors
              })}
            />
          ))}
        </VictoryStack>
        <VictoryScatter
          name={CHART_KEYS.TOTAL_RESOURCES}
          data={totalResources}
          style={styles.totalResourcesMarker.style}
          dataComponent={totalResourcesMarkerComponent}
        />
      </VictoryChart>
    </div>
  );
};

TotalResourcesMarker.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  offset: PropTypes.number,
  style: PropTypes.object
};

BenchOverAllocationChart.propTypes = {
  availabilityData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.arrayOf(PropTypes.string).isRequired,
      bench: PropTypes.number.isRequired,
      notWorking: PropTypes.number.isRequired,
      fittingAllocation: PropTypes.number.isRequired,
      overAllocation: PropTypes.number.isRequired,
      totalResources: PropTypes.number.isRequired
    })
  ),
  loading: PropTypes.bool.isRequired,
  selectedProblem: PropTypes.oneOf(Object.values(PROBLEM_FILTER_OPTIONS))
    .isRequired
};

export default BenchOverAllocationChart;
